import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Circle,
  Play,
  FileText,
  HelpCircle,
  Zap,
  Trophy,
  Star,
  Menu,
  Pencil,
  Plus,
  Trash2,
  Upload,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  getCourseWithCurriculum,
  getLesson,
  updateLesson,
  uploadCourseMedia,
  lmsGetMeLearner,
  lmsGetEnrollmentStatus,
  lmsGetProgress,
  lmsCompleteLesson,
  lmsGetLearnerXp,
  getTutorAssignedCourses,
  type CourseWithCurriculum,
  type LessonDetail,
} from '@/lib/api';
import { getModuleIcon } from '@/lib/moduleIcons';
import { LessonViewSkeleton } from '@/components/skeletons/LessonViewSkeleton';
import { SlideOver } from '@/components/SlideOver';
import { useAuth } from '@/contexts/AuthContext';
import type { LessonBlock, LessonBlockType } from '@/lib/lessonBlocks';
import { createBlock, createQuizQuestion, getQuizQuestions, normalizeQuizBlockForSave, type QuizQuestion } from '@/lib/lessonBlocks';
import { Label } from '@/components/ui/label';

type DisplayType = 'video' | 'text' | 'quiz';

function contentTypeToDisplay(contentType: string): DisplayType {
  if (contentType === 'video' || contentType === 'link') return 'video';
  if (contentType === 'quiz') return 'quiz';
  return 'text';
}

function formatDuration(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
}

const typeIcon = { video: Play, text: FileText, quiz: HelpCircle };

function renderRichText(text: string | undefined) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    const key = `${idx}-${line.slice(0, 10)}`;
    const trimmed = line.trim();
    if (!trimmed) return <br key={key} />;
    // Simple markdown-style headings
    if (trimmed.startsWith('## ')) {
      return (
        <h3 key={key} className="text-base sm:text-lg font-display font-semibold text-foreground mt-3">
          {trimmed.replace(/^##\s+/, '')}
        </h3>
      );
    }
    if (trimmed.startsWith('### ')) {
      return (
        <h4 key={key} className="text-sm sm:text-base font-display font-semibold text-foreground mt-2">
          {trimmed.replace(/^###\s+/, '')}
        </h4>
      );
    }
    // Quote
    if (trimmed.startsWith('> ')) {
      return (
        <blockquote
          key={key}
          className="border-l-4 border-accent pl-3 sm:pl-4 py-2 bg-accent/5 rounded-r-lg text-muted-foreground italic"
        >
          {trimmed.replace(/^>\s+/, '')}
        </blockquote>
      );
    }
    // Bullets and numbered lists inline
    if (trimmed.startsWith('- ')) {
      return (
        <ul key={key} className="list-disc pl-5">
          <li>{trimmed.replace(/^-+\s+/, '')}</li>
        </ul>
      );
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      return (
        <ol key={key} className="list-decimal pl-5">
          <li>{trimmed.replace(/^\d+\.\s+/, '')}</li>
        </ol>
      );
    }
    // Basic bold/italic via markdown markers
    const parts = [];
    let rest = trimmed;
    while (rest.length) {
      const boldMatch = rest.match(/\*\*(.+?)\*\*/);
      const italicMatch = rest.match(/\*(.+?)\*/);
      const nextMatch =
        boldMatch && italicMatch
          ? boldMatch.index! < italicMatch.index!
            ? { type: 'bold', match: boldMatch }
            : { type: 'italic', match: italicMatch }
          : boldMatch
          ? { type: 'bold', match: boldMatch }
          : italicMatch
          ? { type: 'italic', match: italicMatch }
          : null;
      if (!nextMatch) {
        parts.push(rest);
        break;
      }
      const m = nextMatch.match;
      if (m.index! > 0) {
        parts.push(rest.slice(0, m.index));
      }
      const content = m[1];
      if (nextMatch.type === 'bold') {
        parts.push(<strong key={`${key}-b-${parts.length}`}>{content}</strong>);
      } else {
        parts.push(<em key={`${key}-i-${parts.length}`}>{content}</em>);
      }
      rest = rest.slice(m.index! + m[0].length);
    }
    return (
      <p key={key} className="my-1">
        {parts}
      </p>
    );
  });
}

function ProgressBar({ value, className }: { value: number; className?: string }) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div className={cn('h-1.5 w-full rounded-full bg-secondary/40 overflow-hidden', className)}>
      <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function LessonViewPage() {
  const { programSlug, courseSlug, sectionId } = useParams<{ programSlug: string; courseSlug: string; sectionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isTutor = user?.role === 'tutor';
  const [tutorAssignedCourseIds, setTutorAssignedCourseIds] = useState<Set<string>>(new Set());
  const [tutorAssignedProgramSlugs, setTutorAssignedProgramSlugs] = useState<Set<string>>(new Set());
  const [course, setCourse] = useState<CourseWithCurriculum | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLessonIdx, setActiveLessonIdx] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [lessonBlocksById, setLessonBlocksById] = useState<Record<string, LessonBlock[]>>({});
  const [lessonLoadingId, setLessonLoadingId] = useState<string | null>(null);
  const [lessonEditOpen, setLessonEditOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBlocks, setEditBlocks] = useState<LessonBlock[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Record<string, boolean>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, string | null>>({});
  const [uploadTarget, setUploadTarget] = useState<{ blockId: string; type: 'image' | 'video' } | null>(null);
  const [uploadingBlockId, setUploadingBlockId] = useState<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const videoFileInputRef = useRef<HTMLInputElement>(null);
  const guestToastShown = useRef(false);
  const enrollToastShown = useRef(false);

  // LMS: enrollment, progress, XP (signed-in users)
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [completedLessonIds, setCompletedLessonIds] = useState<Set<string>>(new Set());
  const [totalXp, setTotalXp] = useState(0);
  const [courseTotalLessons, setCourseTotalLessons] = useState(0);
  const [progressLoading, setProgressLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [xpJustEarned, setXpJustEarned] = useState<number | null>(null);
  const [displayXp, setDisplayXp] = useState(0); // for animation
  const [quizPassedByLessonId, setQuizPassedByLessonId] = useState<Record<string, boolean>>({});
  const [quizSelections, setQuizSelections] = useState<Record<string, Record<string, string>>>({}); // blockId -> questionId -> optionId
  const [quizChecked, setQuizChecked] = useState<Record<string, boolean>>({}); // blockId -> user has clicked Check
  const [quizQuestionOpen, setQuizQuestionOpen] = useState<Record<string, boolean>>({}); // questionId -> expanded

  // Guest feedback: toast once when viewing lesson without being signed in
  useEffect(() => {
    if (loading || !course || user != null || guestToastShown.current) return;
    guestToastShown.current = true;
    toast.info("You're viewing as a guest. Sign in to save your progress.", { duration: 4500 });
  }, [loading, course, user]);

  // Signed-in (non-admin, non-tutor): encourage enrollment once per visit
  useEffect(() => {
    if (loading || !course || !user || user.role === 'admin' || user.role === 'tutor' || enrollToastShown.current) return;
    enrollToastShown.current = true;
    toast.info('Enroll in this course to save your progress and access all features.', { duration: 5000 });
  }, [loading, course, user]);

  // Load course + sections curriculum
  useEffect(() => {
    if (!programSlug || !courseSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCourseWithCurriculum(programSlug, courseSlug)
      .then((c) => {
        if (!cancelled) setCourse(c);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load course');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [programSlug, courseSlug]);

  useEffect(() => {
    if (!isTutor) return;
    getTutorAssignedCourses()
      .then((data) => {
        setTutorAssignedCourseIds(new Set((data.courses ?? []).map((c) => c.courseId)));
        setTutorAssignedProgramSlugs(new Set((data.programs ?? []).map((p) => p.programSlug)));
      })
      .catch(() => {
        setTutorAssignedCourseIds(new Set());
        setTutorAssignedProgramSlugs(new Set());
      });
  }, [isTutor]);

  const canEditCourse = Boolean(
    course &&
      (isAdmin ||
        (isTutor &&
          (tutorAssignedCourseIds.has(course.id) || tutorAssignedProgramSlugs.has(course.program_slug))))
  );

  const ensureLessonBlocks = async (lessonId: string) => {
    if (!programSlug || !courseSlug || !sectionId) return;
    if (lessonBlocksById[lessonId]) return;
    setLessonLoadingId(lessonId);
    try {
      const detail: LessonDetail = await getLesson(programSlug, courseSlug, sectionId, lessonId);
      setLessonBlocksById((prev) => ({ ...prev, [lessonId]: detail.contentBlocks ?? [] }));
    } catch {
      // ignore for now; UI will show fallback
    } finally {
      setLessonLoadingId((current) => (current === lessonId ? null : current));
    }
  };

  // Load learner, enrollment, progress, XP when user and course are ready
  useEffect(() => {
    if (!user || !course?.id) {
      setEnrollmentId(null);
      setCompletedLessonIds(new Set());
      setTotalXp(0);
      setCourseTotalLessons(0);
      return;
    }
    let cancelled = false;
    const isStaff = user.role === 'admin' || user.role === 'tutor';
    setProgressLoading(true);
    const courseId = course.id;
    const totalLessons = course.sections?.reduce((s, sec) => s + (sec.lessons?.length ?? 0), 0) ?? 0;
    setCourseTotalLessons(totalLessons);
    let currentLearnerId: string | null = null;
    lmsGetMeLearner()
      .then(({ learnerId: lid }) => {
        if (cancelled || lid == null) return null;
        currentLearnerId = lid;
        return lmsGetEnrollmentStatus(lid, courseId);
      })
      .then((status) => {
        if (cancelled || !currentLearnerId || !status) return null;
        if (status.enrolled && status.enrollment) {
          setEnrollmentId(status.enrollment.id);
          return lmsGetProgress(status.enrollment.id);
        }
        // Only redirect to course enroll flow for students; tutors/admins can view lessons without enrolling
        if (!cancelled && programSlug && courseSlug && !isStaff) {
          navigate(`/courses/${programSlug}/courses/${courseSlug}?enroll=1`, { replace: true });
        }
        return null;
      })
      .then((progress) => {
        if (cancelled || !progress) return null;
        setCompletedLessonIds(new Set(progress.completedLessonIds));
        return currentLearnerId ? lmsGetLearnerXp(currentLearnerId) : null;
      })
      .then((xpRes) => {
        if (cancelled) return;
        if (xpRes) {
          setTotalXp(xpRes.totalXp);
          setDisplayXp(xpRes.totalXp);
        }
      })
      .catch(() => {
        if (!cancelled) setEnrollmentId(null);
      })
      .finally(() => {
        if (!cancelled) setProgressLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, course?.id, programSlug, courseSlug, navigate]);

  // Sync displayXp with totalXp when no animation running
  useEffect(() => {
    if (xpJustEarned === null) setDisplayXp(totalXp);
  }, [totalXp, xpJustEarned]);

  // Animate XP counter when we just earned some
  useEffect(() => {
    if (xpJustEarned == null || xpJustEarned <= 0) return;
    const start = totalXp - xpJustEarned;
    setDisplayXp(start);
    const duration = 1200;
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const pct = Math.min(1, elapsed / duration);
      const ease = 1 - (1 - pct) * (1 - pct);
      setDisplayXp(Math.round(start + (totalXp - start) * ease));
      if (pct < 1) requestAnimationFrame(tick);
      else setXpJustEarned(null);
    };
    const id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [xpJustEarned, totalXp]);

  const handleMarkComplete = async () => {
    if (!enrollmentId || !lesson.id || completeLoading) return;
    setCompleteLoading(true);
    try {
      const res = await lmsCompleteLesson(enrollmentId, lesson.id, {
        quizPassed: lessonHasQuiz ? lessonQuizPassed : true,
      });
      setCompletedLessonIds((prev) => new Set([...prev, lesson.id]));
      setTotalXp(res.totalXp);
      if (res.xpAwarded > 0) {
        setXpJustEarned(res.xpAwarded);
        toast.success(`+${res.xpAwarded} XP earned!`);
      } else {
        setDisplayXp(res.totalXp);
        toast.success('Lesson marked complete.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('Quiz')) toast.error('Complete the quiz correctly first.');
      else toast.error(msg || 'Could not mark complete.');
    } finally {
      setCompleteLoading(false);
    }
  };

  const setQuizSelection = (blockId: string, questionId: string, optionId: string) => {
    setQuizSelections((prev) => ({
      ...prev,
      [blockId]: { ...(prev[blockId] ?? {}), [questionId]: optionId },
    }));
  };

  const checkQuiz = (blockId: string, questions: QuizQuestion[]) => {
    setQuizChecked((prev) => ({ ...prev, [blockId]: true }));
    const sel = quizSelections[blockId] ?? {};
    const allCorrect = questions.every((q) => {
      const chosen = sel[q.id];
      const correctOpt = q.options.find((o) => o.correct);
      return correctOpt && chosen === correctOpt.id;
    });
    if (allCorrect) setQuizPassedByLessonId((prev) => ({ ...prev, [lesson.id]: true }));
  };

  const toggleQuizQuestion = (questionId: string) => {
    setQuizQuestionOpen((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  // Preload active lesson blocks when section or active lesson changes
  useEffect(() => {
    if (!programSlug || !courseSlug || !sectionId) return;
    if (!course) return;
    const section = course.sections?.find((s) => s.id === sectionId);
    if (!section || !section.lessons?.length) return;
    const safeIdx = Math.max(0, Math.min(activeLessonIdx, section.lessons.length - 1));
    const current = section.lessons[safeIdx];
    if (!current) return;
    void ensureLessonBlocks(current.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programSlug, courseSlug, sectionId, course, activeLessonIdx]);

  if (!programSlug || !courseSlug || !sectionId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-destructive">Invalid URL.</p>
        <Link to="/courses" className="text-accent hover:underline mt-2 ml-4 inline-block">Back to courses</Link>
      </div>
    );
  }

  if (loading) {
    return <LessonViewSkeleton />;
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-destructive">{error ?? 'Course not found.'}</p>
        <Link to={`/courses/${programSlug}/courses/${courseSlug}`} className="text-accent hover:underline mt-2 ml-4 inline-block">Back to course</Link>
      </div>
    );
  }

  const section = course.sections?.find((s) => s.id === sectionId);
  if (!section) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-destructive">Module not found.</p>
        <Link to={`/courses/${programSlug}/courses/${courseSlug}`} className="text-accent hover:underline mt-2 ml-4 inline-block">Back to course</Link>
      </div>
    );
  }

  const lessons = section.lessons ?? [];
  const backToCourse = `/courses/${programSlug}/courses/${courseSlug}`;
  const sectionCompletedCount = lessons.filter((l) => completedLessonIds.has(l.id)).length;
  const sectionPercent = lessons.length > 0 ? (sectionCompletedCount / lessons.length) * 100 : 0;
  const courseCompletedCount = completedLessonIds.size;
  const courseProgressPercent = courseTotalLessons > 0 ? (courseCompletedCount / courseTotalLessons) * 100 : 0;

  if (lessons.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <p className="text-muted-foreground text-center">This module has no lessons yet.</p>
        <Button asChild className="mt-4 rounded-xl">
          <Link to={backToCourse}><ArrowLeft className="w-4 h-4 mr-2" /> Back to course</Link>
        </Button>
      </div>
    );
  }

  const activeIdx = Math.min(activeLessonIdx, lessons.length - 1);
  const lesson = lessons[activeIdx];
  const displayType = contentTypeToDisplay(lesson.contentType);
  const LessonIcon = typeIcon[displayType];
  const ModuleIcon = getModuleIcon(section.icon);
  const currentLessonBlocks = lessonBlocksById[lesson.id] ?? [];
  const lessonHasQuiz = currentLessonBlocks.some((b) => b.type === 'quiz');
  const lessonQuizPassed = quizPassedByLessonId[lesson.id] === true;
  const isLessonCompleted = completedLessonIds.has(lesson.id);
  const canMarkComplete = enrollmentId && !isLessonCompleted && (lessonHasQuiz ? lessonQuizPassed : true);

  const goToLesson = (idx: number) => {
    const safeIdx = Math.max(0, Math.min(idx, lessons.length - 1));
    const target = lessons[safeIdx];
    setActiveLessonIdx(safeIdx);
    setMobileNavOpen(false);
    if (target) {
      void ensureLessonBlocks(target.id);
    }
  };

  const openLessonEdit = () => {
    setEditTitle(lesson.title);
    const raw = lessonBlocksById[lesson.id] ?? [];
    const normalized = raw.map((b) => {
      if (b.type === 'quiz') {
        const questions = getQuizQuestions(b);
        return { ...b, questions, question: undefined, options: undefined, explanation: undefined };
      }
      return b;
    });
    setEditBlocks(normalized);
    setEditingLessonId(lesson.id);
    setLessonEditOpen(true);
  };

  const saveLessonEdit = async () => {
    if (!programSlug || !courseSlug || !sectionId || !editingLessonId) return;
    setEditSaving(true);
    try {
      const blocksToSave = editBlocks.map((b) =>
        b.type === 'quiz' ? normalizeQuizBlockForSave(b) : b
      );
      const updated = await updateLesson(programSlug, courseSlug, sectionId, editingLessonId, {
        title: editTitle.trim() || 'Untitled lesson',
        contentBlocks: blocksToSave,
      });
      setLessonBlocksById((prev) => ({ ...prev, [editingLessonId]: updated.contentBlocks ?? [] }));
      setLessonEditOpen(false);
      setEditingLessonId(null);
      toast.success('Lesson saved');
      getCourseWithCurriculum(programSlug, courseSlug).then(setCourse).catch(() => {});
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save lesson');
    } finally {
      setEditSaving(false);
    }
  };

  const addBlock = (type: LessonBlockType) => {
    setEditBlocks((prev) => [...prev, createBlock(type)]);
  };

  const updateBlock = (blockId: string, updater: (block: LessonBlock) => LessonBlock) => {
    setEditBlocks((prev) => prev.map((b) => (b.id === blockId ? updater(b) : b)));
  };

  const removeBlock = (blockId: string) => {
    setEditBlocks((prev) => prev.filter((b) => b.id !== blockId));
    setCollapsedBlocks((prev) => {
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
    setImageErrors((prev) => {
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
  };

  const toggleBlockCollapsed = (blockId: string) => {
    setCollapsedBlocks((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
  };

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setEditBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(targetIdx, 0, item);
      return next;
    });
  };

  const validateImageUrl = (blockId: string, url: string) => {
    if (!url) {
      setImageErrors((prev) => ({ ...prev, [blockId]: null }));
      return;
    }
    try {
      const img = new Image();
      img.onload = () => {
        const ratio = img.naturalWidth / img.naturalHeight;
        const err =
          !img.naturalWidth || !img.naturalHeight
            ? 'Could not determine image dimensions.'
            : ratio < 1.3
            ? 'Image must be landscape (wider than tall).'
            : null;
        setImageErrors((prev) => ({ ...prev, [blockId]: err }));
      };
      img.onerror = () => {
        setImageErrors((prev) => ({ ...prev, [blockId]: 'Could not load image from this URL.' }));
      };
      img.src = url;
    } catch {
      setImageErrors((prev) => ({ ...prev, [blockId]: 'Invalid image URL.' }));
    }
  };

  const MAX_IMAGE_MB = 5;
  const MAX_VIDEO_MB = 50; // Supabase free tier project limit

  const handleMediaFile = async (blockId: string, type: 'image' | 'video', file: File | null) => {
    setUploadTarget(null);
    if (!file) return;
    const isImage = type === 'image';
    const maxBytes = isImage ? MAX_IMAGE_MB * 1024 * 1024 : MAX_VIDEO_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(isImage ? `Image must be ${MAX_IMAGE_MB} MB or less.` : `Video must be ${MAX_VIDEO_MB} MB or less.`);
      return;
    }
    setUploadingBlockId(blockId);
    try {
      const { url } = await uploadCourseMedia(file);
      updateBlock(blockId, (b) => {
        if (b.type === 'image') return { ...b, url };
        if (b.type === 'video') return { ...b, url };
        return b;
      });
      if (isImage) validateImageUrl(blockId, url);
      toast.success(isImage ? 'Image uploaded.' : 'Video uploaded.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingBlockId(null);
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="mb-4 p-3 rounded-xl bg-secondary/50 relative">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-accent" />
          <span className="text-xs font-semibold text-foreground">Progress</span>
          {progressLoading ? (
            <span className="ml-auto text-xs text-muted-foreground">Loading…</span>
          ) : (
            <span className="ml-auto text-xs text-accent font-bold tabular-nums transition-all duration-300">{displayXp} XP</span>
          )}
        </div>
        {xpJustEarned != null && xpJustEarned > 0 && (
          <span className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full bg-africa-green text-white text-xs font-bold animate-pulse">
            +{xpJustEarned} XP
          </span>
        )}
        <ProgressBar value={courseProgressPercent} className="h-1.5" />
      </div>
      <div className="mb-4">
        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
          <ModuleIcon className="w-3.5 h-3.5" /> {section.title}
        </p>
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>{sectionCompletedCount}/{lessons.length} lessons</span>
          <span>{Math.round(sectionPercent)}%</span>
        </div>
        <ProgressBar value={sectionPercent} className="h-1.5" />
      </div>
      <nav className="space-y-1 flex-1 overflow-y-auto">
        {lessons.map((l, idx) => {
          const Icon = typeIcon[contentTypeToDisplay(l.contentType)];
          const active = idx === activeIdx;
          const completed = completedLessonIds.has(l.id);
          return (
            <button
              key={l.id}
              onClick={() => goToLesson(idx)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all text-sm ${
                active ? 'bg-accent/10 text-accent border border-accent/20' : 'hover:bg-secondary text-foreground'
              }`}
            >
              {completed ? (
                <CheckCircle2 className="w-4 h-4 text-africa-green flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{l.title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Icon className="w-3 h-3" /> {formatDuration(l.durationMinutes)} · +{l.xpPerLesson} XP
                </p>
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <div className="lg:hidden sticky top-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center justify-between">
        <Link to={backToCourse} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex-1 text-center px-2 min-w-0">
          <p className="text-xs text-muted-foreground truncate">{section.title}</p>
          <p className="text-sm font-semibold text-foreground truncate">{lesson.title}</p>
        </div>
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors shrink-0"
        >
          <Menu className="w-4 h-4" />
        </button>
      </div>

      <div className="lg:hidden px-4 py-2 bg-secondary/30 border-b border-border flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-accent font-semibold tabular-nums">
          <Zap className="w-3 h-3" /> {displayXp} XP
        </div>
        <div className="flex-1">
          <ProgressBar value={sectionPercent} className="h-1" />
        </div>
        <span className="text-xs text-muted-foreground">{activeIdx + 1}/{lessons.length}</span>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row">
        {mobileNavOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
              onClick={() => setMobileNavOpen(false)}
            />
            <div className="fixed top-0 right-0 z-50 h-full w-[300px] sm:w-[340px] bg-card border-l border-border p-4 pt-12 shadow-xl lg:hidden">
              <SidebarContent />
            </div>
          </>
        )}
        <aside className="hidden lg:block w-80 bg-card border-r border-border flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
          <div className="p-4">
            <Link to={backToCourse} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to course
            </Link>
            <SidebarContent />
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <LessonIcon className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted-foreground capitalize">{displayType} Lesson</span>
                  {canEditCourse && (
                    <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={openLessonEdit}>
                      <Pencil className="w-3.5 h-3.5" /> Edit lesson
                    </Button>
                  )}
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-foreground">{lesson.title}</h1>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-accent font-semibold bg-accent/10 px-3 py-1.5 rounded-full self-start whitespace-nowrap">
                <Star className="w-4 h-4" /> +{lesson.xpPerLesson} XP
              </div>
            </div>

            {displayType === 'video' && (
              <div className="mb-6 sm:mb-8">
                <div className="aspect-video rounded-xl sm:rounded-2xl overflow-hidden bg-primary/10 border border-border flex items-center justify-center">
                  {lesson.resourceUrl ? (
                    <iframe
                      src={lesson.resourceUrl}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={lesson.title}
                    />
                  ) : (
                    <Play className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground" />
                  )}
                </div>
              </div>
            )}

            {/* Blocks renderer */}
            <div className="mb-6 sm:mb-8 space-y-5">
              {lessonLoadingId === lesson.id && (
                <p className="text-xs text-muted-foreground">Loading lesson content…</p>
              )}
              {(lessonBlocksById[lesson.id] ?? []).map((block) => {
                switch (block.type) {
                  case 'heading':
                    if (block.level === 2) {
                      return (
                        <h2 key={block.id} className="text-xl sm:text-2xl font-display font-bold text-foreground">
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.level === 3) {
                      return (
                        <h3 key={block.id} className="text-lg sm:text-xl font-display font-semibold text-foreground">
                          {block.text}
                        </h3>
                      );
                    }
                    return (
                      <h4 key={block.id} className="text-base sm:text-lg font-display font-semibold text-foreground">
                        {block.text}
                      </h4>
                    );
                  case 'text':
                    return (
                      <div key={block.id} className="text-sm sm:text-base text-muted-foreground leading-relaxed space-y-1">
                        {renderRichText(block.text)}
                      </div>
                    );
                  case 'bullets':
                    return (
                      <ul key={block.id} className="list-disc pl-5 space-y-1 text-sm sm:text-base text-muted-foreground">
                        {block.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    );
                  case 'numbered':
                    return (
                      <ol key={block.id} className="list-decimal pl-5 space-y-1 text-sm sm:text-base text-muted-foreground">
                        {block.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ol>
                    );
                  case 'quote':
                    return (
                      <blockquote
                        key={block.id}
                        className="border-l-4 border-accent pl-4 py-2 bg-accent/5 rounded-r-lg text-sm sm:text-base text-muted-foreground italic"
                      >
                        {block.text}
                        {block.author && <span className="block mt-1 text-xs text-foreground">— {block.author}</span>}
                      </blockquote>
                    );
                  case 'image':
                    return (
                      <figure key={block.id} className="space-y-2">
                        <img
                          src={block.url}
                          alt={block.alt ?? ''}
                          className="w-full rounded-xl border border-border object-contain"
                        />
                        {block.caption && (
                          <figcaption className="text-xs text-muted-foreground text-center">{block.caption}</figcaption>
                        )}
                      </figure>
                    );
                  case 'video':
                    return (
                      <div key={block.id} className="aspect-video rounded-xl overflow-hidden bg-primary/10 border border-border">
                        {block.url ? (
                          <iframe
                            src={block.url}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={block.title ?? lesson.title}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  case 'quiz': {
                    const questions = getQuizQuestions(block);
                    const checked = quizChecked[block.id];
                    const passed = lessonQuizPassed;
                    const sel = quizSelections[block.id] ?? {};
                    return (
                      <div key={block.id} className="bg-card border border-border rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 space-y-4">
                        <div className="flex items-center gap-2 mb-4 sm:mb-6">
                          <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                          <h2 className="text-lg sm:text-xl font-display font-bold text-foreground">Quick check</h2>
                          {passed && (
                            <span className="ml-auto flex items-center gap-1 text-sm text-africa-green font-medium">
                              <CheckCircle2 className="w-4 h-4" /> Passed
                            </span>
                          )}
                        </div>
                        {questions.map((q, qIdx) => {
                          const isOpen = qIdx === 0 ? true : (quizQuestionOpen[q.id] ?? false);
                          const chosenId = sel[q.id];
                          const correctOpt = q.options.find((o) => o.correct);
                          const showResult = checked && correctOpt;
                          return (
                            <div key={q.id} className={qIdx > 0 ? 'border-t border-border pt-4' : ''}>
                              <button
                                type="button"
                                onClick={() => toggleQuizQuestion(q.id)}
                                className="w-full flex items-center gap-2 text-left"
                              >
                                {isOpen ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                )}
                                <span className="text-base sm:text-lg font-medium text-foreground">
                                  {qIdx + 1}. {q.question}
                                </span>
                                {showResult && chosenId === correctOpt?.id && (
                                  <CheckCircle2 className="w-4 h-4 text-africa-green shrink-0 ml-1" />
                                )}
                                {showResult && chosenId !== correctOpt?.id && (
                                  <XCircle className="w-4 h-4 text-destructive shrink-0 ml-1" />
                                )}
                              </button>
                              {isOpen && (
                                <div className="mt-3 pl-6 space-y-2">
                                  {q.options.map((opt) => {
                                    const isChosen = chosenId === opt.id;
                                    const isCorrect = correctOpt?.id === opt.id;
                                    const showCorrect = checked && isCorrect;
                                    const showWrong = checked && isChosen && !isCorrect;
                                    return (
                                      <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => !passed && setQuizSelection(block.id, q.id, opt.id)}
                                        className={`w-full px-4 py-3 sm:px-5 sm:py-4 rounded-lg sm:rounded-xl border text-left text-sm sm:text-base transition-colors ${
                                          showCorrect
                                            ? 'border-africa-green bg-africa-green/10 text-foreground'
                                            : showWrong
                                            ? 'border-destructive bg-destructive/10 text-foreground'
                                            : isChosen
                                            ? 'border-accent bg-accent/10 text-foreground'
                                            : 'border-border hover:bg-secondary text-foreground'
                                        }`}
                                      >
                                        {opt.text}
                                      </button>
                                    );
                                  })}
                                  {q.explanation && checked && (
                                    <p className="mt-3 text-sm text-muted-foreground italic">{q.explanation}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {!passed && (
                          <Button
                            type="button"
                            onClick={() => checkQuiz(block.id, questions)}
                            className="mt-4 gap-2"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {checked ? 'Check again' : 'Check answers'}
                          </Button>
                        )}
                      </div>
                    );
                  }
                  case 'homework':
                    return (
                      <div key={block.id} className="border border-dashed border-africa-purple/50 rounded-xl p-4 sm:p-5 bg-africa-purple/5">
                        <h3 className="text-sm sm:text-base font-display font-semibold text-foreground mb-1">
                          {block.title ?? 'Homework'}
                        </h3>
                        <p className="text-sm sm:text-base text-muted-foreground whitespace-pre-line">
                          {block.instructions}
                        </p>
                      </div>
                    );
                  default:
                    return null;
                }
              })}

              {(!lessonBlocksById[lesson.id] || lessonBlocksById[lesson.id].length === 0) && (
                <p className="text-sm text-muted-foreground">Lesson content will appear here when available.</p>
              )}

              {/* Mark complete + XP */}
              {enrollmentId && !isAdmin && (
                <div className="py-6 border-t border-border space-y-3">
                  {isLessonCompleted ? (
                    <div className="flex items-center gap-2 text-africa-green font-medium">
                      <CheckCircle2 className="w-5 h-5" />
                      Lesson complete
                    </div>
                  ) : (
                    <>
                      {lessonHasQuiz && !lessonQuizPassed && (
                        <p className="text-sm text-muted-foreground">
                          Pass the quiz above to unlock &quot;Mark complete&quot; and earn +{lesson.xpPerLesson} XP.
                        </p>
                      )}
                      <Button
                        type="button"
                        onClick={handleMarkComplete}
                        disabled={!canMarkComplete || completeLoading}
                        className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl gap-2"
                      >
                        {completeLoading ? (
                          'Saving…'
                        ) : (
                          <>
                            <Trophy className="w-4 h-4" />
                            Mark complete · +{lesson.xpPerLesson} XP
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-row items-center justify-between gap-3 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => goToLesson(activeIdx - 1)}
                disabled={activeIdx === 0}
                className="rounded-xl gap-2 shrink-0"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </Button>
              {activeIdx < lessons.length - 1 ? (
                <Button onClick={() => goToLesson(activeIdx + 1)} className="rounded-xl gap-2 shrink-0">
                  Next <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl gap-2 shrink-0">
                  <Link to={backToCourse}>
                    <Trophy className="w-4 h-4" /> Back to course
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>

      <SlideOver open={lessonEditOpen} onClose={() => { setLessonEditOpen(false); setEditingLessonId(null); }} title="Edit lesson">
        <input
          ref={imageFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (uploadTarget?.type === 'image') handleMediaFile(uploadTarget.blockId, 'image', f ?? null);
          }}
        />
        <input
          ref={videoFileInputRef}
          type="file"
          accept="video/mp4,video/webm"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (uploadTarget?.type === 'video') handleMediaFile(uploadTarget.blockId, 'video', f ?? null);
          }}
        />
        <div className="space-y-4">
          <div>
            <Label className="mb-1">Lesson title</Label>
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="e.g. Introduction" />
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <Label>Content blocks</Label>
              <div className="flex items-center gap-2">
                <select
                  className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  value=""
                  onChange={(e) => {
                    const v = e.target.value as LessonBlockType;
                    if (v) { addBlock(v); e.target.value = ''; }
                  }}
                >
                  <option value="">Add block…</option>
                  <option value="text">Text</option>
                  <option value="heading">Heading</option>
                  <option value="bullets">Bullet list</option>
                  <option value="numbered">Numbered list</option>
                  <option value="quote">Quote</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="quiz">Quiz</option>
                  <option value="homework">Homework</option>
                </select>
              </div>
            </div>
            <div className="space-y-3">
              {editBlocks.map((block, index) => (
                <div key={block.id} className="border border-border rounded-lg p-3 bg-card space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs font-medium text-muted-foreground capitalize"
                      onClick={() => toggleBlockCollapsed(block.id)}
                    >
                      <span className={`inline-block transition-transform ${collapsedBlocks[block.id] ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                      {block.type}
                    </button>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => moveBlock(block.id, 'up')}
                        disabled={index === 0}
                        aria-label="Move block up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => moveBlock(block.id, 'down')}
                        disabled={index === editBlocks.length - 1}
                        aria-label="Move block down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeBlock(block.id)}
                        aria-label="Remove block"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {!collapsedBlocks[block.id] && (
                  <>
                  {block.type === 'text' && (
                    <Textarea
                      value={block.text}
                      onChange={(e) => updateBlock(block.id, (b) => b.type === 'text' ? { ...b, text: e.target.value } : b)}
                      placeholder="Paragraph or intro text…"
                      rows={3}
                      className="resize-y"
                    />
                  )}
                  {block.type === 'heading' && (
                    <div className="space-y-2">
                      <select
                        className="rounded-md border border-input bg-background px-2 py-1.5 text-sm w-full"
                        value={block.level}
                        onChange={(e) => updateBlock(block.id, (b) => b.type === 'heading' ? { ...b, level: Number(e.target.value) as 2 | 3 | 4 } : b)}
                      >
                        <option value={2}>Heading 2</option>
                        <option value={3}>Heading 3</option>
                        <option value={4}>Heading 4</option>
                      </select>
                      <Input
                        value={block.text}
                        onChange={(e) => updateBlock(block.id, (b) => b.type === 'heading' ? { ...b, text: e.target.value } : b)}
                        placeholder="Heading text"
                      />
                    </div>
                  )}
                  {block.type === 'bullets' && (
                    <div className="space-y-2">
                      {(block.items || []).map((item, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={item}
                            onChange={(e) => updateBlock(block.id, (b) => b.type === 'bullets' ? { ...b, items: b.items.map((it, j) => (j === i ? e.target.value : it)) } : b)}
                            placeholder={`Item ${i + 1}`}
                          />
                          <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => updateBlock(block.id, (b) => b.type === 'bullets' ? { ...b, items: b.items.filter((_, j) => j !== i) } : b)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => updateBlock(block.id, (b) => b.type === 'bullets' ? { ...b, items: [...b.items, ''] } : b)}>
                        <Plus className="w-3 h-3 mr-1" /> Add item
                      </Button>
                    </div>
                  )}
                  {block.type === 'numbered' && (
                    <div className="space-y-2">
                      {(block.items || []).map((item, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={item}
                            onChange={(e) => updateBlock(block.id, (b) => b.type === 'numbered' ? { ...b, items: b.items.map((it, j) => (j === i ? e.target.value : it)) } : b)}
                            placeholder={`Step ${i + 1}`}
                          />
                          <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => updateBlock(block.id, (b) => b.type === 'numbered' ? { ...b, items: b.items.filter((_, j) => j !== i) } : b)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => updateBlock(block.id, (b) => b.type === 'numbered' ? { ...b, items: [...b.items, ''] } : b)}>
                        <Plus className="w-3 h-3 mr-1" /> Add step
                      </Button>
                    </div>
                  )}
                  {block.type === 'quote' && (
                    <div className="space-y-2">
                      <Textarea value={block.text} onChange={(e) => updateBlock(block.id, (b) => b.type === 'quote' ? { ...b, text: e.target.value } : b)} placeholder="Quote text" rows={2} />
                      <Input value={block.author ?? ''} onChange={(e) => updateBlock(block.id, (b) => b.type === 'quote' ? { ...b, author: e.target.value || undefined } : b)} placeholder="Author (optional)" />
                    </div>
                  )}
                  {block.type === 'image' && !collapsedBlocks[block.id] && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={uploadingBlockId === block.id}
                          onClick={() => {
                            setUploadTarget({ blockId: block.id, type: 'image' });
                            imageFileInputRef.current?.click();
                          }}
                        >
                          <Upload className="w-3.5 h-3.5" />
                          {uploadingBlockId === block.id ? 'Uploading…' : `Upload image (max ${MAX_IMAGE_MB} MB)`}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">JPEG, PNG or WebP. Image must be landscape (wider than tall).</p>
                      <Label className="text-xs">Or paste URL</Label>
                      <Input
                        value={block.url}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateBlock(block.id, (b) => (b.type === 'image' ? { ...b, url: value } : b));
                          validateImageUrl(block.id, value);
                        }}
                        placeholder="https://…"
                      />
                      <Input value={block.alt ?? ''} onChange={(e) => updateBlock(block.id, (b) => b.type === 'image' ? { ...b, alt: e.target.value || undefined } : b)} placeholder="Alt text (optional)" />
                      <Input value={block.caption ?? ''} onChange={(e) => updateBlock(block.id, (b) => b.type === 'image' ? { ...b, caption: e.target.value || undefined } : b)} placeholder="Caption (optional)" />
                    </div>
                  )}
                  {block.type === 'video' && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={uploadingBlockId === block.id}
                          onClick={() => {
                            setUploadTarget({ blockId: block.id, type: 'video' });
                            videoFileInputRef.current?.click();
                          }}
                        >
                          <Upload className="w-3.5 h-3.5" />
                          {uploadingBlockId === block.id ? 'Uploading…' : `Upload video (max ${MAX_VIDEO_MB} MB)`}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">MP4 or WebM. Or use an embed URL below (e.g. YouTube).</p>
                      <Label className="text-xs">Video URL or embed</Label>
                      <Input value={block.url} onChange={(e) => updateBlock(block.id, (b) => b.type === 'video' ? { ...b, url: e.target.value } : b)} placeholder="https://… or YouTube embed URL" />
                      <Input value={block.title ?? ''} onChange={(e) => updateBlock(block.id, (b) => b.type === 'video' ? { ...b, title: e.target.value || undefined } : b)} placeholder="Title (optional)" />
                    </div>
                  )}
                  {block.type === 'quiz' && (
                    <div className="space-y-4">
                      {getQuizQuestions(block).map((q, qIdx) => (
                        <div key={q.id} className="rounded-lg border border-border p-3 space-y-2 bg-secondary/20">
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-xs">Question {qIdx + 1}</Label>
                            {getQuizQuestions(block).length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive shrink-0"
                                onClick={() =>
                                  updateBlock(block.id, (b) =>
                                    b.type === 'quiz' && b.questions
                                      ? { ...b, questions: b.questions.filter((_, j) => j !== qIdx) }
                                      : b
                                  )
                                }
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove
                              </Button>
                            )}
                          </div>
                          <Input
                            value={q.question}
                            onChange={(e) =>
                              updateBlock(block.id, (b) =>
                                b.type === 'quiz' && b.questions
                                  ? { ...b, questions: b.questions.map((qu, j) => (j === qIdx ? { ...qu, question: e.target.value } : qu)) }
                                  : b
                              )
                            }
                            placeholder="Question text"
                          />
                          <Label className="text-xs">Options</Label>
                          {q.options.map((opt, optIdx) => (
                            <div key={opt.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={opt.correct}
                                onChange={(e) =>
                                  updateBlock(block.id, (b) =>
                                    b.type === 'quiz' && b.questions
                                      ? {
                                          ...b,
                                          questions: b.questions.map((qu, j) =>
                                            j === qIdx ? { ...qu, options: qu.options.map((o, k) => (k === optIdx ? { ...o, correct: e.target.checked } : o)) } : qu
                                          ),
                                        }
                                      : b
                                  )
                                }
                              />
                              <Input
                                value={opt.text}
                                onChange={(e) =>
                                  updateBlock(block.id, (b) =>
                                    b.type === 'quiz' && b.questions
                                      ? {
                                          ...b,
                                          questions: b.questions.map((qu, j) =>
                                            j === qIdx ? { ...qu, options: qu.options.map((o, k) => (k === optIdx ? { ...o, text: e.target.value } : o)) } : qu
                                          ),
                                        }
                                      : b
                                  )
                                }
                                placeholder={`Option ${optIdx + 1}`}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="shrink-0"
                                onClick={() =>
                                  updateBlock(block.id, (b) =>
                                    b.type === 'quiz' && b.questions
                                      ? { ...b, questions: b.questions.map((qu, j) => (j === qIdx ? { ...qu, options: qu.options.filter((_, k) => k !== optIdx) } : qu)) }
                                      : b
                                  )
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              updateBlock(block.id, (b) =>
                                b.type === 'quiz' && b.questions
                                  ? { ...b, questions: b.questions.map((qu, j) => (j === qIdx ? { ...qu, options: [...qu.options, { id: `opt-${Date.now()}-${Math.random().toString(36).slice(2)}`, text: '', correct: false }] } : qu)) }
                                  : b
                              )
                            }
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add option
                          </Button>
                          <Textarea
                            value={q.explanation ?? ''}
                            onChange={(e) =>
                              updateBlock(block.id, (b) =>
                                b.type === 'quiz' && b.questions
                                  ? { ...b, questions: b.questions.map((qu, j) => (j === qIdx ? { ...qu, explanation: e.target.value || undefined } : qu)) }
                                  : b
                              )
                            }
                            placeholder="Explanation (optional)"
                            rows={2}
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateBlock(block.id, (b) =>
                            b.type === 'quiz' ? { ...b, questions: [...getQuizQuestions(b), createQuizQuestion()] } : b
                          )
                        }
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add question
                      </Button>
                    </div>
                  )}
                  {block.type === 'homework' && (
                    <div className="space-y-2">
                      <Input value={block.title ?? ''} onChange={(e) => updateBlock(block.id, (b) => b.type === 'homework' ? { ...b, title: e.target.value || undefined } : b)} placeholder="Title (e.g. Homework)" />
                      <Textarea value={block.instructions} onChange={(e) => updateBlock(block.id, (b) => b.type === 'homework' ? { ...b, instructions: e.target.value } : b)} placeholder="Instructions" rows={3} />
                      <select
                        className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                        value={block.submissionType ?? 'text'}
                        onChange={(e) => updateBlock(block.id, (b) => b.type === 'homework' ? { ...b, submissionType: e.target.value as 'text' | 'file' | 'link' } : b)}
                      >
                        <option value="text">Text</option>
                        <option value="file">File</option>
                        <option value="link">Link</option>
                      </select>
                    </div>
                  )}
                  </>
                  )}
                  {block.type === 'image' && imageErrors[block.id] && !collapsedBlocks[block.id] && (
                    <p className="text-xs text-destructive">{imageErrors[block.id]}</p>
                  )}
                </div>
              ))}
              {editBlocks.length === 0 && (
                <p className="text-sm text-muted-foreground">No blocks yet. Use &quot;Add block…&quot; to add content.</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { setLessonEditOpen(false); setEditingLessonId(null); }} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveLessonEdit}
              disabled={editSaving || !!uploadingBlockId || !editTitle.trim() || Object.values(imageErrors).some((err) => !!err)}
            >
              {editSaving ? 'Saving…' : 'Save lesson'}
            </Button>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}
